// Five filter tabs; Open is the default. Filter logic is win-probability + wasOpportunity-based
// (supersedes the old wonDate/lostDate table) and computed client-side from
// baselineWinChance (0–1) + stage profile + wasOpportunity. All four tabs return real data in Phase 1.

import {bandForWinChance, type StageBand} from './opportunityStage';
// 002 D14: exclude semantics stay single-sourced with the rest of Forecast — the panel writes
// `<key>_operators` beside each selection, and these are the same helpers entityFilterPredicate reads
// them back with.
import {getEntityOperatorKey, FILTER_OPERATOR} from '../shared/components/filters/filter_logic';
import {isProjectOpportunity} from '../shared/util/projectStageUtil';

export type OpportunityFilterTab = 'all' | 'open' | 'won' | 'lost' | 'won_unconverted';

// Tab order: All first, then Open · Won · Lost · Unconverted. All leads the row, but Open is the default
// tab selected on page load (see resolveInitialTab).
// (The `won_unconverted` key keeps its internal name; its on-screen label is "Unconverted" per the design.)
export const OPPORTUNITY_FILTER_TABS: OpportunityFilterTab[] = ['all', 'open', 'won', 'lost', 'won_unconverted'];

export const OPPORTUNITY_TAB_MESSAGE_IDS: Record<OpportunityFilterTab, string> = {
	open: 'opportunities.tab.open',
	won: 'opportunities.tab.won',
	lost: 'opportunities.tab.lost',
	won_unconverted: 'opportunities.tab.won_unconverted',
	all: 'opportunities.tab.all',
};

export const OPPORTUNITY_EMPTY_MESSAGE_IDS: Record<OpportunityFilterTab, string> = {
	open: 'opportunities.empty.open',
	won: 'opportunities.empty.won',
	lost: 'opportunities.empty.lost',
	won_unconverted: 'opportunities.empty.won_unconverted',
	all: 'opportunities.empty.all',
};

export interface OpportunityLike {
	node: {
		stage?: {id?: string | null; name?: string | null; profile?: string | null} | null;
		name?: string | null;
		baselineWinChance?: number | null;
		wasOpportunity?: boolean | null; // R1: persistent "was ever an opportunity" marker; scopes Won
		financialNumbers?: {baselineRevenue?: number | null} | null;
		// Extra fields consumed only by buildOpportunityFilterPredicate (id-based matching against the
		// filter panel selection).
		client?: {id?: string | null; name?: string | null} | null;
		clientContact?: {id?: string | null; firstName?: string | null; lastName?: string | null} | null;
		salesperson?: {id?: string | null; firstName?: string | null; lastName?: string | null} | null;
		projectStartYear?: number | null;
		projectStartMonth?: number | null;
		projectStartDay?: number | null;
		projectEndYear?: number | null;
		projectEndMonth?: number | null;
		projectEndDay?: number | null;
	};
}

// Win probability is stored 0–1. baselineWinChance is non-null (DB default 1.00); null coalesces to
// 1.00 (= 100%). The four states are mutually exclusive, so All = their sum and per-tab counts add up.
// Won additionally requires wasOpportunity (R1) so ordinary delivery projects at the default 1.00 —
// which were never opportunities — are NOT counted as Won.
const wp = (n: OpportunityLike['node']): number => n.baselineWinChance ?? 1;
const isOpen = (n: OpportunityLike['node']): boolean => isProjectOpportunity(n) && wp(n) > 0 && wp(n) < 1;
const isWon = (n: OpportunityLike['node']): boolean => wp(n) === 1 && !isProjectOpportunity(n) && !!n.wasOpportunity;
const isLost = (n: OpportunityLike['node']): boolean => wp(n) === 0;
const isWonUnconverted = (n: OpportunityLike['node']): boolean => wp(n) === 1 && isProjectOpportunity(n);

export function filterOpportunitiesByTab<T extends OpportunityLike>(rows: T[], tab: OpportunityFilterTab): T[] {
	switch (tab) {
		case 'open':
			return rows.filter(r => isOpen(r.node));
		case 'won':
			return rows.filter(r => isWon(r.node));
		case 'lost':
			return rows.filter(r => isLost(r.node));
		case 'won_unconverted':
			return rows.filter(r => isWonUnconverted(r.node));
		case 'all':
		default:
			return rows.filter(r => isOpen(r.node) || isWon(r.node) || isLost(r.node) || isWonUnconverted(r.node));
	}
}

// The row's Opportunity Status — same predicates as the tabs. null = in no bucket (non-OPP 1–99%).
export function deriveOpportunityState(node: OpportunityLike['node']): 'open' | 'won' | 'lost' | 'won_unconverted' | null {
	if (isLost(node)) return 'lost';
	if (isWonUnconverted(node)) return 'won_unconverted';
	if (isWon(node)) return 'won';
	if (isOpen(node)) return 'open';
	return null;
}

// Free-text search over the columns a user would scan for: name, client, contact, salesperson, and the
// project ID — both the custom id (e.g. "ACME-42") and the sequential id, matched with or without the
// leading "P" (so "P1234" and "1234" both hit companyProjectId 1234). Empty/whitespace term matches
// everything. Case-insensitive substring.
export function matchesOpportunitySearch(
	node: {
		name?: string | null;
		client?: {name?: string | null} | null;
		clientContact?: {firstName?: string | null; lastName?: string | null} | null;
		salesperson?: {firstName?: string | null; lastName?: string | null} | null;
		companyProjectId?: number | null;
		customProjectId?: string | null;
	},
	term: string
): boolean {
	const q = term.trim().toLowerCase();
	if (!q) return true;
	const haystacks = [
		node.name,
		node.client?.name,
		`${node.clientContact?.firstName ?? ''} ${node.clientContact?.lastName ?? ''}`,
		`${node.salesperson?.firstName ?? ''} ${node.salesperson?.lastName ?? ''}`,
		node.customProjectId,
		node.companyProjectId != null ? `P${node.companyProjectId}` : null,
		node.companyProjectId != null ? String(node.companyProjectId) : null,
	];
	return haystacks.some(h => (h ?? '').toLowerCase().includes(q));
}

// One sort key per sortable column (every data column header sorts). The two Quote placeholders
// have no data, so they are not sortable (see sortKeyForColumn).
export type OpportunitySortKey =
	| 'name'
	| 'client'
	| 'contact'
	| 'targetValue'
	| 'winChance'
	| 'stage'
	| 'startDate'
	| 'endDate'
	| 'status'
	| 'salesperson'
	| 'quoteStatus'
	| 'quoteExpiry'
	// A custom field column's id (CUSTOM_FIELD_COLUMN_PREFIX + definition id).
	| `customField_${string}`;
// The sort keys of the built-in columns - the ones with a comparator in opportunitySortingFns.
export type NativeOpportunitySortKey = Exclude<OpportunitySortKey, `customField_${string}`>;
export type SortOrder = 'asc' | 'desc';

// The default preference scope — the global /opportunities page. Host components (e.g. the
// client-detail Opportunities tab) pass a distinct scope so their tab/sort/column
// preferences persist independently. Keys are derived from the scope so the default value
// reproduces the original 'opportunities-overview-*' keys exactly — the existing page is unchanged.
export const DEFAULT_PREFERENCE_SCOPE = 'opportunities-overview';
const tabKey = (scope: string): string => `${scope}-tab`;
const sortKey = (scope: string): string => `${scope}-sort`;

export function readSessionTab(scope: string = DEFAULT_PREFERENCE_SCOPE): OpportunityFilterTab | null {
	try {
		const raw = sessionStorage.getItem(tabKey(scope));
		return raw && (OPPORTUNITY_FILTER_TABS as string[]).includes(raw) ? (raw as OpportunityFilterTab) : null;
	} catch {
		return null;
	}
}

export function writeSessionTab(tab: OpportunityFilterTab, scope: string = DEFAULT_PREFERENCE_SCOPE): void {
	try {
		sessionStorage.setItem(tabKey(scope), tab);
	} catch {
		/* sessionStorage unavailable */
	}
}

export function readSessionSort(scope: string = DEFAULT_PREFERENCE_SCOPE): {key: OpportunitySortKey; order: SortOrder} | null {
	try {
		const raw = sessionStorage.getItem(sortKey(scope));
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function writeSessionSort(
	sort: {key: OpportunitySortKey; order: SortOrder},
	scope: string = DEFAULT_PREFERENCE_SCOPE
): void {
	try {
		sessionStorage.setItem(sortKey(scope), JSON.stringify(sort));
	} catch {
		/* sessionStorage unavailable */
	}
}

// Pure tab/sort resolution, extracted so the page component holds no branchy logic (keeps its
// coverage attainable): URL wins, then the stored session tab, then 'open' — the default tab shown when
// the page loads with no URL param or stored tab. (Order is independent of the default: 'all' still leads
// the tab row; only the initially-selected tab is Open.)
export function resolveInitialTab(
	urlTab: string | null | undefined,
	sessionTab: OpportunityFilterTab | null
): OpportunityFilterTab {
	if (urlTab && (OPPORTUNITY_FILTER_TABS as string[]).includes(urlTab)) return urlTab as OpportunityFilterTab;
	return sessionTab ?? 'open';
}

export function nextSortOrder(current: {key: OpportunitySortKey; order: SortOrder}, key: OpportunitySortKey): SortOrder {
	return current.key === key && current.order === 'desc' ? 'asc' : 'desc';
}

// The shared FILTER_V4 panel hands back its selections as `{project: {<filterType>: [values], …}}`.
// We match the six opportunity filters client-side here (keeping stage/band/state derivations single-
// sourced in the feature) rather than in shared filter_util.js. Each present, non-empty filter must
// match (AND); each is include-any within itself. Absent/empty filters pass.
export type OpportunityFilterObject = {project?: Record<string, unknown>} | null | undefined;

const asIds = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/**
 * The value a selected stage is stored as: a band's global id, or one of the two sentinels for the fixed
 * boundaries, which are not bands and so have no id.
 *
 * Keying on the id rather than the name is what lets a saved filter survive a rename — the label is
 * resolved from the live mapping at render, so the filter keeps working and simply shows the new name. A
 * band that was deleted leaves an id naming nothing, and that selection then matches no rows; that is
 * the case the admin page warns about.
 */
const stageKeyFor = (bands: StageBand[], baselineWinChance: number | null | undefined): string => {
	const resolved = bandForWinChance(bands, baselineWinChance);
	switch (resolved.kind) {
		case 'lost':
			return 'LOST';
		case 'won':
			return 'WON';
		case 'band':
			return resolved.band.id;
		default:
			return '';
	}
};

const isExcluded = (sel: Record<string, unknown>, key: string): boolean =>
	(sel[getEntityOperatorKey(key)] as Record<string, boolean> | undefined)?.[FILTER_OPERATOR.EXCLUDE] === true;

/**
 * One field's verdict. An empty selection passes (the field is not filtering); otherwise the field's own
 * match is negated when its Exclude toggle is on. Fields AND together, include and exclude alike, exactly
 * as the shared entityFilterPredicate does elsewhere.
 */
const fieldMatches = (selected: string[], excluded: boolean, hit: boolean): boolean =>
	selected.length === 0 || (excluded ? !hit : hit);

export interface OpportunityFilterPredicateOptions {
	/**
	 * Ignore the panel's Opportunity Status selection.
	 *
	 * Used only by the per-tab counts (002 D8). The page has two status controls — the tabs and the
	 * panel's own Opportunity Status field — and a count that honoured the panel field would make every
	 * non-matching tab read (0): selecting Status = Won would leave four dead tabs and an empty list under
	 * a (0) label. The counts instead answer "if I clicked this tab, ignoring my Status selection, how
	 * many rows?". The accepted trade-off: with a panel status filter active, a count can exceed the rows
	 * shown after clicking it. Every other filter is honoured.
	 */
	ignoreStatusFilter?: boolean;
}

export function buildOpportunityFilterPredicate(
	filterObj: OpportunityFilterObject,
	bands: StageBand[],
	options: OpportunityFilterPredicateOptions = {}
): (node: OpportunityLike['node']) => boolean {
	const sel = (filterObj && filterObj.project) || {};
	const client = asIds(sel['client']);
	const contact = asIds(sel['client_contact']);
	const salesperson = asIds(sel['opportunitySalesperson']);
	const stage = asIds(sel['opportunityStage']);
	const winProbability = asIds(sel['opportunityWinProbability']);
	const status = asIds(sel['opportunityStatus']);

	const clientExcluded = isExcluded(sel, 'client');
	const contactExcluded = isExcluded(sel, 'client_contact');
	const salespersonExcluded = isExcluded(sel, 'opportunitySalesperson');
	const stageExcluded = isExcluded(sel, 'opportunityStage');
	const winProbabilityExcluded = isExcluded(sel, 'opportunityWinProbability');
	const statusExcluded = isExcluded(sel, 'opportunityStatus');

	return (node: OpportunityLike['node']): boolean => {
		if (!fieldMatches(client, clientExcluded, client.includes(node.client?.id ?? ''))) return false;
		if (!fieldMatches(contact, contactExcluded, contact.includes(node.clientContact?.id ?? ''))) return false;
		if (!fieldMatches(salesperson, salespersonExcluded, salesperson.includes(node.salesperson?.id ?? ''))) return false;
		// D14: Stage and Win Probability derive the same key but are two independent fields, each with its
		// own Exclude toggle, so the single shared branch splits into two checks over one derivation.
		if (stage.length || winProbability.length) {
			const key = stageKeyFor(bands, node.baselineWinChance);
			if (!fieldMatches(stage, stageExcluded, stage.includes(key))) return false;
			if (!fieldMatches(winProbability, winProbabilityExcluded, winProbability.includes(key))) return false;
		}
		if (status.length && !options.ignoreStatusFilter) {
			const state = deriveOpportunityState(node);
			if (!fieldMatches(status, statusExcluded, !!state && status.includes(state))) return false;
		}
		return true;
	};
}

/**
 * The applied-filter selection persisted by the shared FILTER_V4 panel, read back for the initial render.
 *
 * The panel restores its own chips from this same localStorage entry on mount but does not push the value
 * to its onFiltersChange handler until the user next touches it — so without seeding the selection here, a
 * saved filter would render as chips yet match nothing on the first load, showing every row. Reading the
 * same entry the panel writes (a `{project: {<filterType>: [ids]}}` object) keeps the two in sync. A stage
 * selection whose band was later deleted survives as an id naming no live band, which then matches zero
 * rows — the deliberate behaviour the admin-page warning covers, rather than silently falling open.
 */
export function readAppliedFilterObject(storageName: string): OpportunityFilterObject {
	try {
		const raw = localStorage.getItem(storageName);
		return raw ? (JSON.parse(raw) as OpportunityFilterObject) : null;
	} catch {
		// A malformed entry is treated as no filter rather than crashing the page on load.
		return null;
	}
}
